import React, { useState, useContext, useEffect } from "react";
import { Tag, User } from "../models/User";
import { SlideModal } from "./SlideModal";
import { AppContext } from "../window";
import { PayModal } from "./PayModal";
import { FavoriteModal } from "./FavoriteModal";
import { FAVORITE_ENDPOINT, Post, SLIDER_SAVE_ENDPOINT } from "../api/events";
import { ModalType, SliderContext } from "./App";
import { NoUserModal } from "./NoUserModal";
import { NoPictureModal } from "./NoPictureModal";
import Button from 'react-bootstrap/Button';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { upgradeAccount } from "../api/userApi";
import { NotificationContext } from "../context/NotificationContext";
import { useNavigate } from "react-router";
import { usePendingPurchase, PurchaseType, getCurrentReturnRoute } from "../context/PendingPurchaseContext";
import { logDataLayerEvent } from "../utils/tagManager";
import { useTranslation } from "../i18n";

export const Slide: React.FC = () => {
  const { user: authorizedUser, texts, setUser: setAuthorizedUser } = useContext(AppContext);
  const { user, setUser, modal, setModal } = useContext(SliderContext);
  const { addNotification } = useContext(NotificationContext);
  const { setPendingPurchase } = usePendingPurchase();
  const navigate = useNavigate();
  const [info, setInfo] = useState(false);
  const [pendingMatchUserId, setPendingMatchUserId] = useState<number | null>(null);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [wasFavorite, setWasFavorite] = useState<boolean>(false);
  const { t } = useTranslation();
  
  // Handler for PayModal upgrade click
  const handleUpgradeClick = async (upgradeName: string) => {
    if (!user) return;
    const purchaseType = upgradeName === 'premium' ? PurchaseType.PREMIUM : PurchaseType.MORE_MATCHES;
    const eventName = purchaseType === PurchaseType.PREMIUM ? 'upgrade_premium' : 'upgrade_matches';
    try {
      const result = await upgradeAccount(upgradeName);
      if (result.success) {
        logDataLayerEvent(eventName, {}, authorizedUser);
        // Update the user's wallet with new premiumExpiry if provided
        if (result.premiumExpiry && authorizedUser && setAuthorizedUser) {
          setAuthorizedUser({
            ...authorizedUser,
            wallet: {
              ...authorizedUser.wallet,
              premiumExpiry: result.premiumExpiry
            }
          });
        }
        // Show notification with upgrade name
        if (texts.upgrades && texts.upgrades[upgradeName]) {
          addNotification({
            level: "info",
            message: texts.upgrades[upgradeName],
          });
        } else {
          addNotification({
            level: "info",
            message: t('slider_app.upgrade_successful'),
          });
        }
        setModal(undefined);
      } else {
        // Save pending purchase before navigating to credits
        const cost = upgradeName === 'premium' ? 250 : 50;
        const notification = upgradeName === 'premium' 
          ? t('slider_app.premium_activated')
          : t('slider_app.matches_added');
        
        setPendingPurchase({
          type: purchaseType,
          cost: cost,
          data: { upgrade: upgradeName },
          returnRoute: getCurrentReturnRoute(),
          notification: notification
        });
        
        navigate('/credits');
      }
    } catch (e) {
      addNotification({
        level: "danger",
        message: t('slider_app.upgrade_failed'),
      });
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      if (typeof user === 'undefined') {
        try {
          const response = await fetch("/user/slider/json");
          const newUser = await response.json();
          
          if (newUser.id) {
            setUser(newUser);
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error('Error loading user:', error);
          setUser(null);
        }
      }
    };
    
    loadUser();
  }, [user, setUser]);

  const getNewUser = async (setUser: (user: User | undefined | null) => void) => {
    try {
      const response = await fetch("/user/slider/json");
      const newUser = await response.json();
      if (newUser.id) {
        setUser(newUser);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error getting new user:', error);
      setUser(null);
    }
  };

  const save = async (authorizedUserId: number | undefined, userId: number, attitude: number) => {
    if (!authorizedUserId) {
      return undefined;
    }
    const response = await Post(SLIDER_SAVE_ENDPOINT, {
      userId: authorizedUserId,
      aboutUserId: userId,
      attitude: attitude
    });
    return JSON.parse(await response.json());
  };

  const requestMatch = async (authorizedUserId : number | undefined, userId: number): Promise<ModalType | undefined> => {
    // Check if the authorized user has a display picture
    if (!authorizedUser?.displayPicture?.id) {
      setPendingMatchUserId(userId);
      return ModalType.NoPicture;
    }
    
    logDataLayerEvent('match_accept', { aboutUserId: userId }, authorizedUser);
    const result = await save(authorizedUserId,userId, 1);
    if (!result) {
      return undefined;
    }
    if (result.reciprocal && result.reciprocal === 1) {
      return ModalType.Match;
    }
    if (result.outOfMatches && result.outOfMatches === 1) {
      return ModalType.Pay;
    }
    return undefined;
  };

  const rejectMatch = async (authorizedUserId : number | undefined, userId: number): Promise<ModalType | undefined> => {
    logDataLayerEvent('match_reject', { aboutUserId: userId }, authorizedUser);
    const result = await save(authorizedUserId,userId, 0);
    if (result && result.outOfMatches && result.outOfMatches === 1) {
      return ModalType.Pay;
    }
    return undefined;
  };

  const addFavorite = async (userId: number) => {
    return fetch(FAVORITE_ENDPOINT.replace(':id', userId + ''));
  };

  // Handler for when a picture is uploaded in the NoPictureModal
  const handlePictureUploaded = async () => {
    if (pendingMatchUserId !== null && user) {
      const userId = pendingMatchUserId;
      // Clear the pending match
      setPendingMatchUserId(null);
      // Close the modal
      setModal(undefined);
      // Call requestMatch again - this time it will skip the picture check and proceed normally
      const modalType = await requestMatch(authorizedUser?.id, userId);
      setModal(modalType);
      if (modalType === undefined) {
        await getNewUser(setUser);
      }
    }
  };

  // Handler for favorite button
  const handleFavorite = async (user: User) => {
    setLoadingAction('favorite');
    setInfo(false);
    try {
      // Remember previous favorite state for modal messaging
      const prevIsFavorite = !!user.isFavoriteOf;
      setWasFavorite(prevIsFavorite);

      await addFavorite(user.id);

      // First show the modal with the previous state
      setModal(ModalType.Favorite);

      // Then update the user to reflect the new state
      setUser({ ...user, isFavoriteOf: 1 } as User);
    } finally {
      setLoadingAction(null);
    }
  };

  if (typeof user === 'undefined') {
    return null;
  }

  if (user === null) {
    return <NoUserModal />;
  }

  let description = user.description;
  if (description?.length > 144) {
    description = user.description.substring(0, 144) + "...";
  }

  const shouldHideToolbar = !!modal && modal !== ModalType.Favorite;
  
  return (
    <div className={`slide position-relative align-items-center flex-grow-1 flex-md-grow-0 d-flex flex-column col-lg-6 offset-lg-3 col-xl-4 offset-xl-4 shadow ${shouldHideToolbar ? 'modal-active' : ''}`} role="alert" aria-live="assertive" aria-atomic="true">
      <div className="slide-picture backdrop flex-grow" style={{ backgroundImage: `url(${user.displayPicture.large})` }}></div>
      <div className="slide-picture flex-grow" style={{ backgroundImage: `url(${user.displayPicture.large})` }}>
        <div className={`thumbnail-overlay upper d-flex align-items-center${modal ? ' dark blur' : ''}`}>
          {modal === ModalType.Match ? <SlideModal onNextUser={async () => {
            getNewUser(setUser);
            setModal(undefined);
          }} /> : null}
          {modal === ModalType.Pay ? <PayModal onUpgradeClick={handleUpgradeClick} /> : null}
          {modal === ModalType.Favorite ? <FavoriteModal wasFavorite={wasFavorite} /> : null}
          {modal === ModalType.NoPicture ? <NoPictureModal onPictureUploaded={handlePictureUploaded} /> : null}

        </div>
          <div className={modal ? "overlay-content fade" : "overlay-content fade show"}>
            <span className="name">{user.nickname.length > 14 ? user.nickname.substring(0, 12) + '..' : user.nickname}</span>
            <span className="location d-flex align-items-center"><LocationOnIcon className="me-1" fontSize="small" /> {user.town}, {user.province.name} </span>
        </div>
      </div>
      <div className={`slide-toolbar ${shouldHideToolbar ? 'modal-active' : ''}`}>
        <div className={info ? 'info full' : 'info'}>
          <div className="details w-100">
            <span className="description d-block mb-2">{description}</span>
            <div className="tags">
              <ul className="list-unstyled d-flex flex-wrap gap-2 mb-0">
                {Object.values(user.tags).map((tag: Tag, index: number) => {
                  if (index > 9) {
                    return;
                  }
                  let highlight = <></>;
                  if (authorizedUser && authorizedUser.tags && tag.id in authorizedUser.tags) {
                    highlight = <StarIcon className="highlight" fontSize="medium" />;
                  }
                  return (
                    <li key={tag.id}>
                      <label
                        className={`btn btn-outline-secondary btn-sm tag mb-0 d-flex align-items-center position-relative`}
                      >
                        <span>{tag.name} {highlight}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </div>
        <div className="buttons d-flex justify-content-between align-items-center p-3">
          <div>
            <Button
              variant="outline-secondary"
              id="favorite"
              className={`rounded-circle shadow-sm btn-slider-action position-relative ${loadingAction === 'favorite' ? 'loading' : ''}`}
              onClick={() => handleFavorite(user)}
              disabled={loadingAction === 'favorite'}
            >
              {user.isFavoriteOf ? <StarIcon fontSize="medium" /> : <StarBorderIcon fontSize="small" />}
            </Button>
          </div>
          <div>
            <Button 
              variant="outline-primary" 
              id="reject"
              className={`rounded-circle shadow-sm btn-slider-action ${loadingAction === 'reject' ? 'loading' : ''}`}
              disabled={loadingAction === 'reject'}
              onClick={async () => {
                setLoadingAction('reject');
                setInfo(false);
                try {
                  const modalType = await rejectMatch(authorizedUser?.id,user.id);
                  setModal(modalType);
                  if (modalType === undefined) {
                    await getNewUser(setUser);
                  }
                } finally {
                  setLoadingAction(null);
                }
              }}
            >
              <CloseIcon fontSize="large" />
            </Button>
          </div>
          <div>
            <Button 
              variant="primary" 
              id="accept"
              className={`rounded-circle shadow-sm btn-slider-action ${loadingAction === 'like' ? 'loading' : ''}`}
              disabled={loadingAction === 'like'}
              onClick={async () => {
                setLoadingAction('like');
                setInfo(false);
                try {
                  const modalType = await requestMatch(authorizedUser?.id,user.id);
                  setModal(modalType);
                  if (modalType === undefined) {
                    await getNewUser(setUser);
                  }
                } finally {
                  setLoadingAction(null);
                }
              }}
            >
              <FavoriteIcon fontSize="large" />
            </Button>
          </div>
          <div>
            <Button variant="outline-secondary" id="more" className="rounded-circle shadow-sm btn-slider-action align-middle" onClick={() => setInfo(!info)}><MoreVertIcon fontSize="small" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
};